/**
 * ChannelDiscoveryPage — S21
 *
 * Authenticated route /discover — browse publicly listed channels and request access.
 *
 * HARD INVARIANTS (do not relax):
 *  - D-S48 membership-first: NEVER renders a buy-credits button or link for any
 *    viewer (non-member, pending, or approved). This module must NOT import any
 *    credit/purchase hook.
 *  - D-S22 (by omission): performs ONLY self-service join requests. Does NOT
 *    import or call useApproveAccessRequest / useDenyAccessRequest /
 *    change_member_role / remove_member / add_member or any member-management
 *    mutation.
 *  - B-2026-0105: template_count is always 0 — never surface it.
 */

import { useState, useCallback, useEffect } from 'react';
import { useDiscoverChannels } from '@/hooks/useDiscoverChannels';
import { useChannelMemberships } from '@/hooks/useChannelMemberships';
import { useCreateAccessRequest } from '@/hooks/useChannelAccessRequests';
import { ChannelDiscoveryCard } from '@/components/channels/ChannelDiscoveryCard';
import type { ChannelPublicResponse, ChannelDiscoverySort } from '@/types/channel';
import type { ChannelRequestedRole } from '@/services/channelApi';
import { isAxiosError } from 'axios';

/** Toast notification shown after a successful join request. */
function SuccessToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 200,
        padding: '12px 20px',
        borderRadius: '12px',
        backgroundColor: 'var(--color-success, #10B981)',
        color: '#fff',
        fontSize: '14px',
        fontWeight: 500,
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        maxWidth: '360px',
      }}
    >
      <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.172l7.879-7.879a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      {message}
    </div>
  );
}

/** Skeleton card for loading state. */
function SkeletonCard() {
  return (
    <div
      aria-hidden="true"
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '16px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: 'var(--color-dark-elevated-2)', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ height: '14px', borderRadius: '6px', backgroundColor: 'var(--color-dark-elevated-2)', width: '60%', marginBottom: '8px' }} />
          <div style={{ height: '11px', borderRadius: '6px', backgroundColor: 'var(--color-dark-elevated-2)', width: '35%' }} />
        </div>
      </div>
      <div style={{ height: '22px', borderRadius: '11px', backgroundColor: 'var(--color-dark-elevated-2)', width: '30%' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ height: '11px', borderRadius: '4px', backgroundColor: 'var(--color-dark-elevated-2)', width: '100%' }} />
        <div style={{ height: '11px', borderRadius: '4px', backgroundColor: 'var(--color-dark-elevated-2)', width: '80%' }} />
      </div>
      <div style={{ height: '38px', borderRadius: '10px', backgroundColor: 'var(--color-dark-elevated-2)', marginTop: 'auto' }} />
    </div>
  );
}

/**
 * Inner card wrapper that holds per-card mutation state.
 * This ensures each card gets its own useCreateAccessRequest hook call
 * (hooks must not be called conditionally, so we need a stable component per slug).
 */
function DiscoveryCardWrapper({
  channel,
  relationship,
  onJoinSuccess,
}: {
  channel: ChannelPublicResponse;
  relationship: 'approved' | 'pending' | 'none';
  onJoinSuccess: (slug: string) => void;
}) {
  const createRequest = useCreateAccessRequest(channel.slug);

  const handleRequestJoin = useCallback(
    async (_slug: string, message: string, role: ChannelRequestedRole) => {
      try {
        await createRequest.mutateAsync({ message, requested_role: role });
        onJoinSuccess(channel.slug);
      } catch (err) {
        // Surface 409 "already requested" as a user-friendly error
        if (isAxiosError(err) && err.response?.status === 409) {
          throw new Error('You have already submitted a request to join this channel.');
        }
        throw err instanceof Error ? err : new Error('Failed to submit request. Please try again.');
      }
    },
    [createRequest, channel.slug, onJoinSuccess],
  );

  return (
    <ChannelDiscoveryCard
      channel={channel}
      relationship={relationship}
      onRequestJoin={handleRequestJoin}
    />
  );
}

function ChannelDiscoveryPage() {
  // ── Filter / sort state ────────────────────────────────────────────────────
  const [sportFilter, setSportFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState<ChannelDiscoverySort>('newest');

  // ── In-session pending set (slugs submitted this session) ─────────────────
  // useCreateAccessRequest does NOT invalidate memberships — track locally.
  const [pendingSlugs, setPendingSlugs] = useState<Set<string>>(new Set());

  // ── Toast state ────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<string | null>(null);

  // ── Data fetching via useInfiniteQuery ─────────────────────────────────────
  // Changing filters resets the query key which resets pagination to page 1.
  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useDiscoverChannels({
    q: searchQuery || undefined,
    sport_focus: sportFilter || undefined,
    sort,
    page_size: 20,
  });

  const { data: memberships } = useChannelMemberships();

  // Build approved membership set: channel slugs the user is approved for
  const approvedSlugs = new Set<string>(
    (memberships ?? []).map((m) => m.channel_slug),
  );

  // Flatten all pages into a single list for rendering
  const accumulatedItems: ChannelPublicResponse[] = data?.pages.flatMap((p) => p.items) ?? [];
  const totalCount = data?.pages[0]?.total ?? 0;

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleFilterChange = useCallback((newSport: string) => {
    setSportFilter(newSport);
  }, []);

  const handleSearchChange = useCallback((q: string) => {
    setSearchQuery(q);
  }, []);

  const handleSortChange = useCallback((s: ChannelDiscoverySort) => {
    setSort(s);
  }, []);

  const handleJoinSuccess = useCallback((slug: string) => {
    setPendingSlugs((prev) => new Set([...prev, slug]));
    setToast('Your request has been sent. The coach will review it shortly.');
  }, []);

  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  // ── Relationship resolver ──────────────────────────────────────────────────
  const getRelationship = (slug: string): 'approved' | 'pending' | 'none' => {
    if (approvedSlugs.has(slug)) return 'approved';
    if (pendingSlugs.has(slug)) return 'pending';
    return 'none';
  };

  const totalLabel =
    totalCount > 0
      ? `${totalCount} channel${totalCount === 1 ? '' : 's'}`
      : null;

  return (
    <div
      style={{
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '32px 24px 64px',
      }}
    >
      {/* Page header */}
      <div style={{ marginBottom: '28px' }}>
        <h1
          style={{
            margin: 0,
            fontSize: '26px',
            fontWeight: 800,
            fontFamily: 'var(--font-heading)',
            color: 'var(--color-text-primary)',
            letterSpacing: '-0.02em',
          }}
        >
          Discover Channels
        </h1>
        <p style={{ margin: '6px 0 0', fontSize: '14px', color: 'var(--color-text-muted)' }}>
          Find a coaching channel and request access to get started.
        </p>
      </div>

      {/* Filter / search bar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: '24px',
          alignItems: 'center',
        }}
      >
        {/* Search box */}
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '180px', maxWidth: '340px' }}>
          <svg
            style={{
              position: 'absolute',
              left: '11px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '16px',
              height: '16px',
              color: 'var(--color-text-muted)',
              pointerEvents: 'none',
            }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1116.65 16.65z" />
          </svg>
          <input
            type="search"
            placeholder="Search channels…"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label="Search channels by name"
            style={{
              width: '100%',
              padding: '9px 12px 9px 34px',
              borderRadius: '10px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-2)',
              color: 'var(--color-text-primary)',
              fontSize: '14px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Sport filter */}
        <input
          type="text"
          placeholder="Filter by sport…"
          value={sportFilter}
          onChange={(e) => handleFilterChange(e.target.value)}
          aria-label="Filter channels by sport"
          style={{
            flex: '1 1 160px',
            minWidth: '140px',
            maxWidth: '240px',
            padding: '9px 12px',
            borderRadius: '10px',
            border: '1px solid var(--color-border-subtle)',
            backgroundColor: 'var(--color-dark-elevated-2)',
            color: 'var(--color-text-primary)',
            fontSize: '14px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        {/* Sort control */}
        <select
          value={sort}
          onChange={(e) => handleSortChange(e.target.value as ChannelDiscoverySort)}
          aria-label="Sort channels"
          style={{
            padding: '9px 12px',
            borderRadius: '10px',
            border: '1px solid var(--color-border-subtle)',
            backgroundColor: 'var(--color-dark-elevated-2)',
            color: 'var(--color-text-primary)',
            fontSize: '14px',
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          <option value="newest">Newest first</option>
          <option value="name">Name (A–Z)</option>
        </select>

        {/* Result count label */}
        {totalLabel && !isLoading && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: '13px',
              color: 'var(--color-text-muted)',
              whiteSpace: 'nowrap',
            }}
          >
            {totalLabel}
          </span>
        )}
      </div>

      {/* Loading state — skeleton grid */}
      {isLoading && accumulatedItems.length === 0 && (
        <div
          aria-label="Loading channels"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '18px',
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div
          role="alert"
          style={{
            padding: '40px 24px',
            textAlign: 'center',
            color: 'var(--color-danger)',
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '16px',
          }}
        >
          <p style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600 }}>
            Could not load channels
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            style={{
              padding: '8px 20px',
              borderRadius: '10px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-2)',
              color: 'var(--color-text-primary)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isError && accumulatedItems.length === 0 && (
        <div
          style={{
            padding: '64px 24px',
            textAlign: 'center',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-dark-elevated-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <svg style={{ width: '24px', height: '24px', color: 'var(--color-text-muted)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="10" strokeWidth="1.5" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
            </svg>
          </div>
          <p style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
            {sportFilter || searchQuery ? 'No channels match your filters' : 'No channels yet'}
          </p>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-muted)' }}>
            {sportFilter || searchQuery
              ? 'Try adjusting the search or sport filter.'
              : 'Check back later as new channels are added.'}
          </p>
        </div>
      )}

      {/* Channel grid */}
      {accumulatedItems.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '18px',
          }}
        >
          {accumulatedItems.map((channel) => (
            <DiscoveryCardWrapper
              key={channel.slug}
              channel={channel}
              relationship={getRelationship(channel.slug)}
              onJoinSuccess={handleJoinSuccess}
            />
          ))}
        </div>
      )}

      {/* Load more */}
      {hasNextPage && !isFetchingNextPage && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
          <button
            type="button"
            onClick={handleLoadMore}
            style={{
              padding: '10px 32px',
              borderRadius: '10px',
              border: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-dark-elevated-2)',
              color: 'var(--color-text-primary)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 120ms ease',
            }}
          >
            Load more
          </button>
        </div>
      )}

      {/* Loading indicator for subsequent pages */}
      {isFetchingNextPage && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
          <span
            style={{
              width: '20px',
              height: '20px',
              border: '2px solid var(--color-border-subtle)',
              borderTopColor: 'var(--color-accent)',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.7s linear infinite',
            }}
            aria-label="Loading more channels"
          />
        </div>
      )}

      {/* Success toast */}
      {toast && (
        <SuccessToast
          message={toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default ChannelDiscoveryPage;
